import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import BoldLayout from "../bold-layout";
import { useFeedbackFlow } from "../../hooks/use-feedback-flow";
import type {
	ApiPage,
	ApiPageField,
	Brandkit,
	FormBrand,
} from "../../types/api.types";
import {
	buildFieldSchema,
	type DynamicFormData,
} from "../../utils/field-helpers";

interface StepNegativeFeedbackProps {
	brandkit: Brandkit;
	brand: FormBrand;
	showBackButton?: boolean;
	onBack?: () => void;
	stepKey?: string;
	pageConfig: ApiPage;
	isDarkMode?: boolean;
}

export default function StepNegativeFeedback({
	brandkit,
	brand,
	showBackButton,
	onBack,
	stepKey,
	pageConfig,
	isDarkMode,
}: StepNegativeFeedbackProps) {
	const {
		data,
		setContextText,
		setUserInfo,
		nextStep,
		saveCurrentStepData,
		isSubmitting,
		submitError,
	} = useFeedbackFlow();
	const [error, setError] = useState<string | null>(null);

	const visibleFields = useMemo(
		() =>
			(pageConfig.fields ?? [])
				.filter((f) => f.is_visible)
				.sort((a, b) => a.sort_order - b.sort_order),
		[pageConfig.fields],
	);

	const schema = useMemo(() => {
		const shape: Record<string, z.ZodTypeAny> = {};
		for (const field of visibleFields) {
			shape[field.field_key] = buildFieldSchema(field);
		}
		return z.object(shape);
	}, [visibleFields]);

	const defaultValues = useMemo(() => {
		const values: Record<string, string> = {};
		const userInfo = data.userInfo as unknown as Record<string, unknown>;
		for (const field of visibleFields) {
			if (field.field_key === "contentText") {
				values[field.field_key] = data.contentText ?? "";
			} else {
				values[field.field_key] =
					(userInfo[field.field_key] as string) ?? "";
			}
		}
		return values;
	}, [visibleFields, data.userInfo, data.contentText]);

	const {
		register,
		handleSubmit,
		formState: { errors, isValid },
	} = useForm<DynamicFormData>({
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		resolver: zodResolver(schema) as any,
		mode: "onChange",
		defaultValues,
	});

	const onSubmit = async (formData: DynamicFormData) => {
		const userInfoUpdate: Record<string, string> = {};
		for (const field of visibleFields) {
			if (field.field_key === "contentText") {
				setContextText(formData[field.field_key] || "");
			} else {
				userInfoUpdate[field.field_key] =
					formData[field.field_key] || "";
			}
		}
		if (Object.keys(userInfoUpdate).length > 0) {
			setUserInfo(userInfoUpdate);
		}

		try {
			setError(null);
			await saveCurrentStepData();
			nextStep();
		} catch (err) {
			setError(
				submitError ||
					"Failed to submit your feedback. Please try again.",
			);
		}
	};

	const brandLogo =
		brandkit?.logo_icon_with_label_url ||
		brandkit?.logo_icon_only_url ||
		brandkit?.logo_label_only_url ||
		brandkit?.logo_inverted_icon_with_label_url ||
		brandkit?.logo_inverted_icon_only_url ||
		brandkit?.logo_inverted_label_only_url ||
		null;

	const renderField = (field: ApiPageField) => {
		if (field.field_type === "textarea") {
			return (
				<Textarea
					key={field.field_key}
					label={field.label}
					placeholder={field.placeholder ?? undefined}
					rows={5}
					required={field.is_required}
					error={
						errors[field.field_key]?.message as string | undefined
					}
					{...register(field.field_key)}
					isDark={isDarkMode}
				/>
			);
		}

		const inputType =
			field.field_type === "tel"
				? "tel"
				: field.field_type === "email"
					? "email"
					: field.field_type === "url"
						? "url"
						: "text";

		return (
			<Input
				key={field.field_key}
				label={field.label}
				type={inputType}
				placeholder={field.placeholder ?? undefined}
				required={field.is_required}
				error={errors[field.field_key]?.message as string | undefined}
				{...register(field.field_key)}
				isDark={isDarkMode}
			/>
		);
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="contents">
			<BoldLayout
				showBranding={brand?.branding !== "disabled"}
				brandName={brand.name}
				brandLogo={brandLogo}
				brandkit={brandkit}
				stepKey={stepKey}
				showBackButton={showBackButton}
				onBack={onBack}
				title={pageConfig.attributes.title}
				description={pageConfig.attributes.description}
				isFormSubmit
				continueDisabled={!isValid || isSubmitting}
				continueLabel={isSubmitting ? "Submitting..." : "Submit"}
				isDarkMode={isDarkMode}
			>
				<div className="space-y-8">
					{visibleFields.map(renderField)}
				</div>

				{error && (
					<div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
						<p className="text-sm text-red-600">{error}</p>
						<button
							type="submit"
							className="mt-2 text-sm text-red-700 underline hover:no-underline"
						>
							Try Again
						</button>
					</div>
				)}
			</BoldLayout>
		</form>
	);
}
